import { z } from 'zod';
import { activeProviderTypes, providerTypes } from '../constants';

// All possible internal schema fields from INTERNAL_SCHEMA.md
export const internalTargetFields = [
  // Required Fields
  'group_id',
  'email',
  'number_of_participants',
  // Primary Invoice Fields
  'invoice_company',
  'invoice_vat_number',
  'invoice_vat_liable',
  'invoice_first_name',
  'invoice_last_name',
  'invoice_street',
  'invoice_house_number',
  'invoice_zip',
  'invoice_city',
  'invoice_country',
  // Split Billing Fields (P2)
  'p2_email',
  'p2_invoice_company',
  'p2_invoice_vat_number',
  'p2_invoice_vat_liable',
  'p2_invoice_first_name',
  'p2_invoice_last_name',
  'p2_invoice_email',
  'p2_invoice_street',
  'p2_invoice_house_number',
  'p2_invoice_zip',
  'p2_invoice_city',
  'p2_invoice_country',
  // Activity Fields (P1)
  'activity_1',
  'activity_2',
  'activity_3',
  'activity_4',
  'activity_5',
  // Activity Fields (P2)
  'p2_activity_1',
  'p2_activity_2',
  'p2_activity_3',
  'p2_activity_4',
  'p2_activity_5',
  // Calculation Triggers
  'calc_single_room',
  'calc_insurance',
  // Operational / Logic Fields
  'logic_split_billing',
  'logic_notes',
] as const;

export const mappingSchema = z.object({
  id: z.string().uuid().optional(),
  provider: z.enum(providerTypes),
  sourceFieldId: z.string().min(1, 'Source field ID is verplicht'),
  internalTarget: z.enum(internalTargetFields),
});

export const createMappingSchema = mappingSchema.omit({ id: true }).extend({
  provider: z.enum(activeProviderTypes),
});
export const updateMappingSchema = mappingSchema.partial().omit({ id: true, provider: true });

export type MappingFormData = z.infer<typeof createMappingSchema>;
export type MappingSchema = z.infer<typeof mappingSchema>;
export type InternalTargetField = (typeof internalTargetFields)[number];

// Field metadata for UI display
export interface FieldMetadata {
  key: InternalTargetField;
  label: string;
  category: 'required' | 'participant' | 'primary' | 'split' | 'calculation' | 'logic';
  type: 'string' | 'email' | 'number' | 'boolean' | 'array';
  description?: string;
  required?: boolean;
}

export const fieldMetadata: FieldMetadata[] = [
  // Required Fields
  {
    key: 'group_id',
    label: 'Groep ID',
    category: 'required',
    type: 'string',
    description: 'Verplicht: groep identifier',
    required: true,
  },
  {
    key: 'email',
    label: 'E-mailadres',
    category: 'required',
    type: 'email',
    description: 'Verplicht: primair e-mailadres',
    required: true,
  },

  {
    key: 'number_of_participants',
    label: 'Aantal deelnemers',
    category: 'participant',
    type: 'number',
    description: 'Aantal deelnemers in de registratie',
  },
  {
    key: 'p2_email',
    label: 'P2 E-mailadres',
    category: 'participant',
    type: 'email',
    description: 'Optioneel',
  },

  // Primary Invoice Fields
  {
    key: 'invoice_company',
    label: 'Bedrijfsnaam',
    category: 'primary',
    type: 'string',
    description: 'Bedrijfsnaam voor factuur',
  },
  {
    key: 'invoice_vat_number',
    label: 'BTW Nummer',
    category: 'primary',
    type: 'string',
    description: 'BTW-nummer (BE0...)',
  },
  {
    key: 'invoice_vat_liable',
    label: 'BTW-plichtig',
    category: 'primary',
    type: 'boolean',
    description: 'Ja/Nee (0 of 1)',
  },
  {
    key: 'invoice_first_name',
    label: 'Voornaam',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_last_name',
    label: 'Achternaam',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_street',
    label: 'Straatnaam',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_house_number',
    label: 'Huisnummer',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_zip',
    label: 'Postcode',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_city',
    label: 'Stad',
    category: 'primary',
    type: 'string',
  },
  {
    key: 'invoice_country',
    label: 'Land',
    category: 'primary',
    type: 'string',
    description: '2-letter code (bijv. BE)',
  },

  // Split Billing Fields (P2)
  {
    key: 'p2_invoice_company',
    label: 'P2 Bedrijfsnaam',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_vat_number',
    label: 'P2 BTW Nummer',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_vat_liable',
    label: 'P2 BTW-plichtig',
    category: 'split',
    type: 'boolean',
    description: 'Ja/Nee (0 of 1)',
  },
  {
    key: 'p2_invoice_first_name',
    label: 'P2 Voornaam',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_last_name',
    label: 'P2 Achternaam',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_email',
    label: 'P2 Factuur E-mailadres',
    category: 'split',
    type: 'email',
  },
  {
    key: 'p2_invoice_street',
    label: 'P2 Straatnaam',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_house_number',
    label: 'P2 Huisnummer',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_zip',
    label: 'P2 Postcode',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_city',
    label: 'P2 Stad',
    category: 'split',
    type: 'string',
  },
  {
    key: 'p2_invoice_country',
    label: 'P2 Land',
    category: 'split',
    type: 'string',
  },

  // Activity Fields (P1)
  { key: 'activity_1', label: 'Activiteit 1', category: 'calculation', type: 'string' },
  { key: 'activity_2', label: 'Activiteit 2', category: 'calculation', type: 'string' },
  { key: 'activity_3', label: 'Activiteit 3', category: 'calculation', type: 'string' },
  { key: 'activity_4', label: 'Activiteit 4', category: 'calculation', type: 'string' },
  { key: 'activity_5', label: 'Activiteit 5', category: 'calculation', type: 'string' },

  // Activity Fields (P2)
  { key: 'p2_activity_1', label: 'P2 Activiteit 1', category: 'calculation', type: 'string' },
  { key: 'p2_activity_2', label: 'P2 Activiteit 2', category: 'calculation', type: 'string' },
  { key: 'p2_activity_3', label: 'P2 Activiteit 3', category: 'calculation', type: 'string' },
  { key: 'p2_activity_4', label: 'P2 Activiteit 4', category: 'calculation', type: 'string' },
  { key: 'p2_activity_5', label: 'P2 Activiteit 5', category: 'calculation', type: 'string' },

  // Calculation Triggers
  {
    key: 'calc_single_room',
    label: 'Eenpersoonskamer',
    category: 'calculation',
    type: 'boolean',
    description: 'Ja/Nee - wenst een eenpersoonskamer',
  },
  {
    key: 'calc_insurance',
    label: 'Verzekering',
    category: 'calculation',
    type: 'string',
  },
  // Operational / Logic Fields
  {
    key: 'logic_split_billing',
    label: 'Split Facturatie',
    category: 'logic',
    type: 'boolean',
    description: 'Activeert tweede factuur',
  },
  {
    key: 'logic_notes',
    label: 'Opmerkingen',
    category: 'logic',
    type: 'string',
    description: 'Interne notities',
  },
];
